/**
 * The GlotWise overlap — which marketplace listings also hold a platform account, and which
 * of them you already have in your External Contacts.
 *
 * ## The question it answers
 *
 * A marketplace listing and an external contact can be the same professional reached two ways.
 * The design says so in five filters and two marks:
 *
 * | where | what it says |
 * |---|---|
 * | Marketplace card | `GlotWise Agency` and how complete that profile is, or *Replies by email* |
 * | Marketplace card | *In your contacts* — they are already in your External directory |
 * | External card | *On GlotWise Marketplace* — this contact also offers services there |
 *
 * ## Why it is seeded
 *
 * ⚠ **Nothing records the overlap.** There is no column, no join table and no flag: an imported
 * listing and a contact row have no way to know they are the same person. The 16 September audit
 * recorded this as blocked; it is unblocked only in the sense that David asked for dummy data
 * ("crea datos dummy y listo"), so the shape can be built and the facts cannot.
 *
 * The prototype does the same thing and says so: *"Three ProZ imports are marked as GlotWise
 * sign-ups and six External freelancers appear as GlotWise members, **all sample facts built for
 * the mockup**."* This keeps that restraint — a small, deterministic minority, never a majority,
 * and every mark carries a hover that says it is sample data.
 *
 * ## How it disappears
 *
 * Called from `DirectoriesDataService` only, which only the rebuilt directory provides. When a
 * real link exists, delete the two `seed*` calls and map the real flag onto the same row fields.
 */

/** Plan names a GlotWise account can hold, smallest first — the same ladder as `TIER_RANK`. */
export const GW_TIERS = ['Freel', 'Freel+', 'Pro', 'Team', 'Agency', 'Enterprise'];

/** What a listing is reached by. Two values, because that is the decision a reader makes. */
export const GW_SOURCE_OPTIONS = ['Full registration', 'Replies by email'];
export const GW_PROFILE_OPTIONS = ['Complete', 'Not finished', 'Not on GlotWise'];
export const GW_CONNECTION_OPTIONS = ['In your External Contacts', 'Not connected yet'];
export const GW_LISTED_OPTIONS = ['Listed on GlotWise Marketplace', 'Not listed'];

/** How a listing is reached, in the words the design uses. */
export const REACH_REGISTERED = 'On GlotWise';
export const REACH_MAIL = 'Replies by email · may take longer';

/**
 * The same fact, short enough for a table row.
 *
 * The long form is the design's, and it is right on a card; in the Member column it pushed the
 * listing's Origin and Expert languages — which are real, fetched data — out to an ellipsis. The
 * qualifier that got cut is the softest part of the sentence, so it moves to the hover.
 */
export const REACH_MAIL_SHORT = 'Replies by email';

/** What a GlotWise account on a listing looks like. All of it seeded. */
export interface GlotwiseAccount {
  tier: string;
  /** Profile completeness, 0–100. 100 prints a tick, anything less prints the number. */
  profilePercent: number;
  /** Sections still empty on that profile, for the hover. */
  profileMissing: string[];
  /** ISO date they signed up. */
  since: string;
}

/** The sample marks always say they are samples; a reader must never take one for a fact. */
export const GW_SAMPLE_NOTE = 'Sample data — nothing records the overlap between a marketplace listing and a platform account yet.';

function hashOf(text: string): number {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return Math.abs(hash);
}

function pick(id: string, salt: string, max: number): number {
  return hashOf(`${salt}:${id}`) % max;
}

const MISSING_SECTIONS = ['Rates', 'CAT tools', 'Sample work', 'Fields of expertise', 'Headline'];

/**
 * The GlotWise account behind a marketplace listing, or undefined for an import that has none.
 *
 * ⚠ Invented. **One listing in eight**, deliberately a small minority: the marketplace is an
 * import, and most of it has never touched the platform.
 */
export function seedGlotwiseAccount(listingId: string): GlotwiseAccount | undefined {
  if (pick(listingId, 'gwMember', 8) !== 0) return undefined;

  const complete = pick(listingId, 'gwComplete', 3) !== 0;
  const missingCount = complete ? 0 : 1 + pick(listingId, 'gwMissingN', 2);
  const start = pick(listingId, 'gwMissingAt', MISSING_SECTIONS.length);

  const since = new Date();
  since.setDate(since.getDate() - 30 - pick(listingId, 'gwSince', 500));

  return {
    tier: GW_TIERS[pick(listingId, 'gwTier', GW_TIERS.length)],
    // A finished profile is 100; an unfinished one lands somewhere readable, never 0.
    profilePercent: complete ? 100 : 55 + pick(listingId, 'gwPct', 8) * 5,
    profileMissing: complete ? [] : Array.from({ length: missingCount }, (_, i) => MISSING_SECTIONS[(start + i) % MISSING_SECTIONS.length]),
    since: since.toISOString().slice(0, 10),
  };
}

/**
 * Whether this listing is already one of your External Contacts.
 *
 * ⚠ Invented, and rarer still — **one listing in twenty**. Only an account holder can be, because
 * a contact you hold is somebody you reached, and an import you have never reached is not one.
 */
export function seedAlreadyAContact(listingId: string, hasAccount: boolean): boolean {
  return hasAccount && pick(listingId, 'gwInDirectory', 3) === 0;
}

/**
 * Whether this external contact also offers services on the marketplace.
 *
 * ⚠ Invented. **One contact in six**, the other side of the same overlap.
 */
export function seedListedOnMarketplace(contactId: string): boolean {
  return pick(contactId, 'onMarketplace', 6) === 0;
}

/** "GlotWise Agency", the chip beside a listing's name. */
export function glotwiseStanding(account: GlotwiseAccount | undefined): string {
  return account ? `GlotWise ${account.tier}` : '';
}

/** "profile complete" / "profile 70 %", the line under it. */
export function glotwiseProfileText(account: GlotwiseAccount | undefined): string {
  if (!account) return '';
  return account.profilePercent >= 100 ? 'profile complete' : `profile ${account.profilePercent} %`;
}

/** The hover on the standing chip: what the account is, what is missing, and that it is sample. */
export function glotwiseTip(account: GlotwiseAccount | undefined): string {
  if (!account) return `${REACH_MAIL}\n\n${GW_SAMPLE_NOTE}`;
  const missing = account.profileMissing.length ? `\nStill empty: ${account.profileMissing.join(', ')}` : '';
  return `${glotwiseStanding(account)} · ${glotwiseProfileText(account)}${missing}\n\n${GW_SAMPLE_NOTE}`;
}
